import React, { useState } from 'react';
import { uuid } from 'utils/dom';
import { i18n } from 'utils/i18n';
import FieldControl from 'components/Form/FieldControl';
import Button from 'components/Button';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { analyticsInteraction } from 'utils/analytics';
import ANALYTICS from 'constants/analytics';
import CapsLockControl from '../CapsLockControl';

/**
 * PasswordField Description:
 * an input field with type "password" and a button to toggle type "text"
 *
 * @param {string} name: unique field name
 * @param {string} id - unique field id
 * @param {string} label - a descriptive label for the user to read
 * @param {boolean} full - will make the field fill the width of its container
 * @param {boolean} error - Show Error State
 * @param {boolean} attention - Show Attention State
 * @param {boolean} checkCapsLock - Show Caps Lock State
 * @param {Array | boolean} validations
 * @param {object} breakpoint - Breakpoint selector data
 * @return {JSX} ...
 */
const PasswordField = ({
  name,
  id,
  label,
  full,
  error,
  attention,
  validations,
  disabled,
  checkCapsLock,
  breakpoint,
  className,
  isSignInModal = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const fieldId = uuid();
  const { isDesktop } = breakpoint;

  const toggleShowPassword = () => {
    // The analytics function reads the button's innerText when clicked, but the text updates too quickly,
    // causing incorrect values. To fix this, setState is wrapped in setTimeout,
    // ensuring analytics captures the correct button text.
    setTimeout(() => {
      setShowPassword(!showPassword);
      const loginDialog = document.querySelector('#login-dialog');
      const key = isSignInModal ? ANALYTICS.MODAL : ANALYTICS.DRAWER;
      if (isSignInModal || !loginDialog) {
        analyticsInteraction(key, ANALYTICS.SIGN_IN, showPassword ? 'password hide' : 'password show');
      }
    }, 0);
  };

  const renderField = (capsLockControlProps = {}) => (
    <FieldControl
      name={name}
      id={id || fieldId}
      label={label}
      full={full}
      error={error}
      attention={attention}
      validations={validations}
      disabled={disabled}
      type={!disabled && showPassword ? 'text' : 'password'}
      className={cn('field-control--password', className)}
      autoComplete='current-password'
      {...capsLockControlProps}
    >
      <input />
      <Button
        link
        className='field-control--show-password-btn'
        onClick={toggleShowPassword}
        type='button'
        disabled={disabled}
      >
        {!disabled && i18n(showPassword ? 'field_password_hide' : 'field_password_show')}
      </Button>
    </FieldControl>
  );

  return isDesktop && checkCapsLock ? <CapsLockControl>{renderField}</CapsLockControl> : renderField();
};

PasswordField.defaultProps = {
  checkCapsLock: true,
};

PasswordField.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  full: PropTypes.bool,
  error: PropTypes.bool,
  attention: PropTypes.bool,
  validations: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  disabled: PropTypes.bool,
  checkCapsLock: PropTypes.bool,
  breakpoint: PropTypes.object,
  className: PropTypes.string,
  isSignInModal: PropTypes.bool,
};

export default PasswordField;
